import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Stats from "@/components/landing/Stats";

const CHARTS_IMG = "https://images.unsplash.com/photo-1543286386-713bdd548da4?q=80&w=1200&auto=format&fit=crop";

const services = [
  {
    num: "01",
    name: "Tax Preparation",
    body: "Income tax, corporation tax, VAT and RCT — prepared, optimised and filed with Revenue deadlines always in view.",
    testid: "service-tax",
  },
  {
    num: "02",
    name: "Bookkeeping",
    body: "Clean, current ledgers kept monthly or quarterly. Walk into any conversation with your bank or Revenue fully prepared.",
    testid: "service-bookkeeping",
  },
  {
    num: "03",
    name: "Audit & Assurance",
    body: "Independent statutory audits carried out with rigour and discretion, giving stakeholders confidence in your figures.",
    testid: "service-audit",
  },
  {
    num: "04",
    name: "Business Advisory",
    body: "Cash-flow planning, company structure, pricing and growth strategy — practical counsel grounded in your real numbers.",
    testid: "service-advisory",
  },
  {
    num: "05",
    name: "Payroll",
    body: "Full-service payroll covering PAYE, PRSI and USC, with payslips, Revenue reporting and pension auto-enrolment handled.",
    testid: "service-payroll",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] },
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative bg-navy py-24 text-cream lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div {...fadeUp}>
          <p className="overline-label mb-6 flex items-center gap-4" data-testid="services-overline">
            <span className="inline-block h-px w-10 bg-gold" />
            What We Do
          </p>
          <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl" data-testid="services-heading">
            Five disciplines,<br />one <em className="italic text-gold">standard.</em>
          </h2>
        </motion.div>

        <div className="mt-14 lg:mt-16">
          <Stats />
        </div>

        <div className="mt-14 grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div>
              {services.map((s, i) => (
              <motion.div
                key={s.num}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="group cursor-pointer border-b border-gold/25 py-8 transition-colors duration-500 first:border-t hover:bg-forest/60 lg:py-9"
                data-testid={s.testid}
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              >
                <div className="flex items-center justify-between gap-6 px-2 lg:px-4">
                  <div className="flex items-baseline gap-6">
                    <span className="font-serif text-lg italic text-gold/70">{s.num}</span>
                    <div>
                      <h3 className="font-serif text-2xl font-medium transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">
                        {s.name}
                      </h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-cream/60 sm:text-base">{s.body}</p>
                    </div>
                  </div>
                  <ArrowUpRight
                    size={26}
                    className="shrink-0 text-gold/50 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold"
                  />
                </div>
              </motion.div>
            ))}
            </div>
          </div>

          <div className="hidden lg:col-span-4 lg:col-start-9 lg:block">
            <motion.div {...fadeUp} className="sticky top-32">
              <div className="relative">
                <div className="absolute -right-4 -top-4 h-full w-full border border-gold/40" aria-hidden="true" />
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={CHARTS_IMG}
                    alt="Pen resting on financial charts"
                    className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                    data-testid="services-image"
                  />
                  <div className="absolute inset-0 bg-navy/25" />
                </div>
              </div>
              <p className="mt-6 font-serif text-lg italic text-cream/60">
                The old tools, applied with a new precision.
              </p>
              <button
                data-testid="services-cta-contact"
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-8 border border-gold/60 px-7 py-3.5 font-sans text-sm font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy"
              >
                Discuss Your Needs
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
